import { COMMISSION_RESULT_FAIL_REASON } from './constants';

/**
 * The ComponentCountryCode, ComponentGeneration, ComponentSolshareVersion, and ComponentErrorType types
 * are used to resolve the error component paths and route parameters
 *
 * for example:
 * ./{ComponentSolshareVersion}/{ComponentGeneration}?/{ComponentErrorType}.{ComponentCountryCode}.vue
 * ./Mk1/G2/CTNegativeReversed.AU.vue
 * ./Mk2/CTNegativeReversed.AU.vue
 * 
 * These constants are defined to give flexibility to name / locate the folders and filenames:
 * - SOLSHARE_VERSION   mapped to folder name ComponentSolshareVersion (Mk1 or Mk2)
 * - GENERATION         mapped to folder ComponentGeneration (G1 or G2)
 * - COMMISSION_RESULT_FAIL_REASON mapped to filenames in ComponentErrorType
 * - COUNTRY_CODE       mapped to file extension ComponentCountryCode
 */
export type ComponentCountryCode = 'AU' | 'UK'; 
export type ComponentGeneration = 'G1' | 'G2';
export type ComponentSolshareVersion = 'Mk1' | 'Mk2'; 
export type ComponentErrorType =
  | 'CTNegativeReversed'
  | 'CTNotReading'
  | 'CTReadingIncorrect'
  | 'CTMissing'
  | 'InputMissingVoltage'
  | 'InverterVoltageDetected'
  | 'UnstableConditions'
  | 'UnexpectedSolarCurrent'
  | 'TestCurrentTooSmall'
  | 'CriticalFailure';

/**
 * Array of all valid ComponentErrorType values for runtime checks
 */
export const COMPONENT_ERROR_TYPES: readonly ComponentErrorType[] = [
  'CTNegativeReversed',
  'CTNotReading',
  'CTReadingIncorrect',
  'CTMissing',
  'InputMissingVoltage',
  'InverterVoltageDetected',
  'UnstableConditions',
  'UnexpectedSolarCurrent',
  'TestCurrentTooSmall',
  'CriticalFailure',
] as const;

/**
 * Error types that have pages only for Mk2; Mk1 devices show the default error screen for these.
 */
export const MK2_ONLY_ERROR_TYPES: readonly ComponentErrorType[] = [
  'CriticalFailure',
  'CTMissing',
  'InverterVoltageDetected',
  'UnstableConditions',
  'UnexpectedSolarCurrent',
  'TestCurrentTooSmall',
];

export const DEFAULT_ERROR_COMPONENT = 'DefaultErrorScreen.vue';

/**
 * The ComponentErrorTypeDescription is used to display the title, subtitle, and button text for the error router page component
 * Title: The title of the error type / comment out to remove the title
 * Subtitle: The subtitle of the error type / comment out to remove the subtitle
 * ButtonText: The text of the button to troubleshoot the error type / comment out to remove the button
 * Order: The order of the error type in the error router page component.
 * Particularly helpful if we need the user to do the troubleshooting in a specific order (check all connections then restart for example).
 * ContactButtonText: The text of the button to contact support for the error type / comment out to remove the button
 * NoteText: The text of the note to display for the error type / comment out to remove the note
 */
export const ComponentErrorTypeDescription: Record<
  ComponentErrorType,
  { 
    testId: number; 
    title?: string; 
    subtitle?: string; 
    buttonText?: string; 
    order?: number,
    contactButtonText?: string 
    noteText?: string
  }
> = {
  CTNegativeReversed: {
    testId: 1,
    title: 'CT is reading negative, reverse polarity',
    buttonText: 'Troubleshoot: CT is reversed',
    order: 1,
  },
  CTNotReading: {
    testId: 2,
    title: 'CT not reading',
    buttonText: 'Troubleshoot: CT not reading',
    order: 2,
  },
  CTReadingIncorrect: {
    testId: 3,
    title: 'CT reading incorrect',
    buttonText: 'Troubleshoot: CT reading incorrect',
    order: 3,
  },
  InputMissingVoltage: {
    testId: 4,
    title: 'Tenancy Connection(s) Missing Voltage',
    buttonText: 'Troubleshoot: Tenancy missing voltage',
    order: 4,
  },
  InverterVoltageDetected: {
    testId: 5,
    title: 'Inverter production detected',
    buttonText: 'Troubleshoot: Inverter production detected',
    order: 98, // High number so that it sits next to the "Fixed? Re-try commissioning" button / Reconfigure button
  },
  UnstableConditions: {
    testId: 6,
    title: 'Unstable condition detected',
    subtitle: 'Unstable conditions',
    buttonText: 'Troubleshoot: Unstable conditions',
    order: 99, // High number so that it sits next to the "Fixed? Re-try commissioning" button / Reconfigure button
  },
  UnexpectedSolarCurrent: {
    testId: 7,
    title: 'Unexpected solar current',
    buttonText: 'Troubleshoot: Unexpected solar current',
    order: 7,
  },
  TestCurrentTooSmall: {
    testId: 8,
    title: 'Test current too small',
    buttonText: 'Troubleshoot: Test current too small',
    order: 8,
  },
  CTMissing: {
    testId: 9,
    title: 'No test current',
    buttonText: 'Troubleshoot: No test current',
    order: 9,
  },
  CriticalFailure: {
    testId: 10,
    title: 'Critical failure',
    //buttonText: 'Troubleshoot: Critical failure',
    order: 10,
    contactButtonText: 'Contact Support', 
    noteText: 'Turn off the SolShare AC isolators (Grid) and Inverter isolator and contact Allume Support.',
  }
};

export enum ERROR_CTA_BUTTONS {
  // The button at the error splash screen (Retry commissioning or Reconfigure)
  RETRY_COMMISSIONING='RetryCommissioning',
  RECONFIGURE='Reconfigure', 

  // The button at the troubleshooting page (Try again or Contact support)
  TRY_AGAIN='TryAgain',
  CONTACT_SUPPORT='ContactSupport',
}

export const COMMISSION_RESULT_CONFIGS= {
  [COMMISSION_RESULT_FAIL_REASON.inverter_off_timeout]: {
    failId: 'Com Error #1',
    ctaButtons: [
      ERROR_CTA_BUTTONS.RETRY_COMMISSIONING,
      ERROR_CTA_BUTTONS.RECONFIGURE,
      //ERROR_CTA_BUTTONS.TRY_AGAIN,
      ERROR_CTA_BUTTONS.CONTACT_SUPPORT,
    ],
  },
  [COMMISSION_RESULT_FAIL_REASON.grid_voltage_invalid]: {
    failId: 'Com Error #2',
    ctaButtons: [
      ERROR_CTA_BUTTONS.RETRY_COMMISSIONING,
      ERROR_CTA_BUTTONS.RECONFIGURE,
      //ERROR_CTA_BUTTONS.TRY_AGAIN,
      //ERROR_CTA_BUTTONS.CONTACT_SUPPORT,
    ],
  },
  [COMMISSION_RESULT_FAIL_REASON.safe_state_timeout]: {
    failId: 'Com Error #3',
    ctaButtons: [
      //ERROR_CTA_BUTTONS.RETRY_COMMISSIONING,
      ERROR_CTA_BUTTONS.RECONFIGURE,
      //ERROR_CTA_BUTTONS.TRY_AGAIN,
      ERROR_CTA_BUTTONS.CONTACT_SUPPORT,
    ],
  },
  [COMMISSION_RESULT_FAIL_REASON.measurement_timeout]: {
    failId: 'Com Error #4',
    ctaButtons: [
      ERROR_CTA_BUTTONS.RETRY_COMMISSIONING,
      ERROR_CTA_BUTTONS.RECONFIGURE,
      ERROR_CTA_BUTTONS.TRY_AGAIN,
      //ERROR_CTA_BUTTONS.CONTACT_SUPPORT,
    ],
  },
  [COMMISSION_RESULT_FAIL_REASON.generic_timeout]: {
    failId: 'Com Error #5',
    ctaButtons: [
      ERROR_CTA_BUTTONS.RETRY_COMMISSIONING,
      ERROR_CTA_BUTTONS.RECONFIGURE,
      ERROR_CTA_BUTTONS.TRY_AGAIN,
      //ERROR_CTA_BUTTONS.CONTACT_SUPPORT,
    ],
  },
  [COMMISSION_RESULT_FAIL_REASON.grid_unstable_start]: {
    failId: 'Com Error #6',
    ctaButtons: [
      ERROR_CTA_BUTTONS.RETRY_COMMISSIONING,
      ERROR_CTA_BUTTONS.RECONFIGURE,
      ERROR_CTA_BUTTONS.TRY_AGAIN,
      //ERROR_CTA_BUTTONS.CONTACT_SUPPORT,
    ],
  },
  [COMMISSION_RESULT_FAIL_REASON.solar_unexpected_start]: {
    failId: 'Com Error #7',
    ctaButtons: [
      ERROR_CTA_BUTTONS.RETRY_COMMISSIONING,
      ERROR_CTA_BUTTONS.RECONFIGURE,
      ERROR_CTA_BUTTONS.TRY_AGAIN,
      ERROR_CTA_BUTTONS.CONTACT_SUPPORT,
    ],
  },
  [COMMISSION_RESULT_FAIL_REASON.solar_unexpected_end]: {
    failId: 'Com Error #8',
    ctaButtons: [
      ERROR_CTA_BUTTONS.RETRY_COMMISSIONING,
      ERROR_CTA_BUTTONS.RECONFIGURE,
      ERROR_CTA_BUTTONS.TRY_AGAIN,
      ERROR_CTA_BUTTONS.CONTACT_SUPPORT,
    ],
  },
  [COMMISSION_RESULT_FAIL_REASON.grid_unstable_end]: {
    failId: 'Com Error #9',
    ctaButtons: [
      ERROR_CTA_BUTTONS.RETRY_COMMISSIONING,
      ERROR_CTA_BUTTONS.RECONFIGURE,
      ERROR_CTA_BUTTONS.TRY_AGAIN,
      //ERROR_CTA_BUTTONS.CONTACT_SUPPORT,
    ],
  },
  [COMMISSION_RESULT_FAIL_REASON.grid_unstable_during]: {
    failId: 'Com Error #10',
    ctaButtons: [
      ERROR_CTA_BUTTONS.RETRY_COMMISSIONING,
      ERROR_CTA_BUTTONS.RECONFIGURE,
      ERROR_CTA_BUTTONS.TRY_AGAIN,
      //ERROR_CTA_BUTTONS.CONTACT_SUPPORT,
    ],
  },
  [COMMISSION_RESULT_FAIL_REASON.solar_observed_bad]: {
    failId: 'Com Error #11',
    ctaButtons: [
      ERROR_CTA_BUTTONS.RETRY_COMMISSIONING,
      ERROR_CTA_BUTTONS.RECONFIGURE,
      ERROR_CTA_BUTTONS.TRY_AGAIN,
      ERROR_CTA_BUTTONS.CONTACT_SUPPORT,
    ],
  },
  [COMMISSION_RESULT_FAIL_REASON.ct_swapped]: {
    failId: 'Com Error #12',
    ctaButtons: [
      ERROR_CTA_BUTTONS.RETRY_COMMISSIONING,
      ERROR_CTA_BUTTONS.RECONFIGURE,
      ERROR_CTA_BUTTONS.TRY_AGAIN,
      //ERROR_CTA_BUTTONS.CONTACT_SUPPORT,
    ],
  },
  [COMMISSION_RESULT_FAIL_REASON.ct_reversed]: {
    failId: 'Com Error #13',
    ctaButtons: [
      ERROR_CTA_BUTTONS.RETRY_COMMISSIONING,
      ERROR_CTA_BUTTONS.RECONFIGURE,
      //ERROR_CTA_BUTTONS.TRY_AGAIN,
      //ERROR_CTA_BUTTONS.CONTACT_SUPPORT,
    ],
  },
  [COMMISSION_RESULT_FAIL_REASON.current_diff_unexpected]: {
    failId: 'Com Error #14',
    ctaButtons: [
      ERROR_CTA_BUTTONS.RETRY_COMMISSIONING,
      ERROR_CTA_BUTTONS.RECONFIGURE,
      ERROR_CTA_BUTTONS.TRY_AGAIN,
      //ERROR_CTA_BUTTONS.CONTACT_SUPPORT,
    ],
  },
  [COMMISSION_RESULT_FAIL_REASON.ct_missing]: {
    failId: 'Com Error #15',
    ctaButtons: [
      ERROR_CTA_BUTTONS.RETRY_COMMISSIONING,
      //ERROR_CTA_BUTTONS.RECONFIGURE,
      ERROR_CTA_BUTTONS.TRY_AGAIN,
    ],
  },
}